/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import React, {useEffect, useRef, useState} from 'react';
import {useHistory} from "react-router-dom";
import TabbedContainer from "../../../../../components/common/TabbedContainer";

import {EnteralFormulasSearchPage} from "../../EnteralFormulas";
import {NeofaxPedsMonographSearchPanel} from "./components";
import {CommonFooter} from "../../../../../components/common/footers/Footer";
import CommonOverflowModalPopups from "../../../../../components/common-overflow-menu/CommonOverflowModalPopups";
import {
    SECTION_TYPE_FORMULAS,
    SECTION_TYPE_DRUGS,
    DRUG_MONOGRAPHS_TAB_NAME,
    ENTERAL_FORMULA_TAB_NAME,
    NEOPED_URL, RESULT_TYPE_NEOFAX
} from "../../../common/constants";
import {updateSectionParamInUrl, isNeofaxMenuItem} from "../../../utils";
import {setSelectedTabIndex} from "./redux/neofax_search_redux";
import {usePrefix} from "@carbon/react";
import {useDispatch} from "react-redux";
import {
    TITLE_DRUG_MONOGRAPHS_SEARCH,
    TITLE_ENTERAL_FORMULAS
} from "../../../../../components/common/constants";

export const NeonatalPediatricsSearchPage = (props) => {
    const scrollingDivRef = useRef(null);
    const history = useHistory();
    const location = props.location;
    const dispatch = useDispatch();
    const prefix = usePrefix();
    const [shouldUseDefaults, setShouldUseDefaults] = useState(false);
    const [shouldResetFormulas, setShouldResetFormulas] = useState(false);
    const [previousSection, setPreviousSection] = useState(null);

    const searchParams = new URLSearchParams(location.search);
    const sectionFromUrl = searchParams.get("section") || SECTION_TYPE_DRUGS;
    const [currentSection, setCurrentSection] = useState(sectionFromUrl);
    const isSearchPage = location.pathname !== "/neoped/monograph";
    const redirectLink = "/neoped?section=" + currentSection + "&tab=" + RESULT_TYPE_NEOFAX;

    useEffect(() => {
        const sectionsToResetOnTransition = {
            [SECTION_TYPE_FORMULAS]: { stateSetter: setShouldResetFormulas },
            // Add new sections with their reset state setters:
            // SECTION_TYPE_NAME: { stateSetter: setShouldResetName },
        };
        
        if (previousSection !== null && previousSection !== currentSection) {
            Object.values(sectionsToResetOnTransition).forEach(({ stateSetter }) => {
                stateSetter(false);
            });

            if (sectionsToResetOnTransition[currentSection]) {
                sectionsToResetOnTransition[currentSection].stateSetter(true);
            }
        }
        
        setPreviousSection(currentSection);
    }, [currentSection, previousSection]);

    useEffect(() => {
        if (!isSearchPage) {
            return;
        }
        document.title = currentSection === SECTION_TYPE_DRUGS
                ? TITLE_DRUG_MONOGRAPHS_SEARCH
                : TITLE_ENTERAL_FORMULAS;
    }, [isSearchPage, currentSection]);

    useEffect(() => {
        updateSectionParamInUrl(currentSection, history)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSection]);

    useEffect(() => {
        const handleMenuItemClick = (event) => {
            if (isNeofaxMenuItem(event, prefix) && !window.location.href.includes(NEOPED_URL)) {
                setCurrentSection(SECTION_TYPE_DRUGS);
                dispatch(setSelectedTabIndex(0));
                setShouldUseDefaults(true);
                setShouldResetFormulas(true);
            }
        }

        document.addEventListener('mousedown', handleMenuItemClick, true);

        return () => {
            document.removeEventListener('mousedown', handleMenuItemClick, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sectionTypeTabs = [
        {
            label: DRUG_MONOGRAPHS_TAB_NAME,
            content: <NeofaxPedsMonographSearchPanel shouldUseDefaults={shouldUseDefaults}/>,
            onClick: () => {
                setCurrentSection(SECTION_TYPE_DRUGS);
                if (history.location.pathname + history.location.search !== redirectLink) {
                    dispatch(setSelectedTabIndex(0));
                    history.push(redirectLink);
                }
            }
        },
        {
            label: ENTERAL_FORMULA_TAB_NAME,
            content: <EnteralFormulasSearchPage 
                        setCurrentSection={setCurrentSection} 
                        shouldReset={shouldResetFormulas}
                    />,
            onClick: () => {
                setCurrentSection(SECTION_TYPE_FORMULAS);
                if (history.location.pathname + history.location.search !== redirectLink) {
                    history.push(redirectLink);
                }
            }
        }
    ];

    const containerIndex = currentSection === SECTION_TYPE_FORMULAS ? 1 : 0;

    return (
        <div ref={scrollingDivRef} className="main-container neofax-search-container">
            <div className="empty-container"></div>
            <div className="main-content">
                <div
                    className="mdx-scroll-area mdx-scroll-area-ie"
                    data-testid="neofax-pediatrics-search-page-container"
                >
                    <TabbedContainer
                        className="gray-tabs"
                        tabs={sectionTypeTabs}
                        defaultSelectedIndex={containerIndex}
                        preSelectedIndex={containerIndex}
                        ariaLabel="Neofax/Pediatrics and Enteral Formulas tabs"
                    />
                    <CommonFooter/>
                </div>
            </div>
            <CommonOverflowModalPopups/>
        </div>
    );
};
